import ProjectUtils from "../utils/project-utils";
import {exportXlsx} from "./excel-export-utils.js";
import {parse, isValid, isAfter, addMinutes, addHours, addDays, addMonths, addYears, format, isBefore, endOfDay, startOfDay, startOfMinute} from "date-fns";
import { createSearchParams } from "react-router";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;
const exportableTableDefinitions = new Map();

const ReportUtils = {

    alertErrorMessage(message, title = "", useHtml = false) {
        ProjectUtils.swalErrorMessage(title, message, "Ok", false, "Não", undefined, useHtml);
    },

    applyLoad(setStateHandlers = {}) {
        const {setLoading, clearContent} = setStateHandlers;

        this.clearExportableTables();
        setLoading?.(true);
        clearContent?.();
    },

    catchErrors(error, handlers = {}) {
        const { setLoading, alertFn } = handlers;

        let message = "Não foi possível realizar a pesquisa.";
        if (error?.response?.status !== 500) message = error?.response?.data?.message || message;

        setLoading?.(false);

        if (alertFn) {
            alertFn(message);
        } else {
            this.alertErrorMessage(message);
        }
    },

    periodValidate(startVal, endVal, periodValue, periodType, obligatory = true, customNameDtStart = null, customNameDtEnd = null) {

        if (!startVal && obligatory)
            return { ok: false, message: `O campo "${customNameDtStart ?? "Data/hora inicial"}" é obrigatório.` };

        if (!endVal && obligatory)
            return { ok: false, message:`O campo "${customNameDtEnd ?? "Data/hora final"}" é obrigatório.`};

        const isValidTimeOnly = (value) => {
            return /^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/.test(value);
        };

        const referenceDate = new Date();
        const toDate = (v) => {
            if (v instanceof Date) return v;

            if (typeof v === "string") {
                let fullWithSeconds = parse(v, "dd/MM/yyyy HH:mm:ss", referenceDate);
                if (isValid(fullWithSeconds)) return fullWithSeconds;

                let full = parse(v, "dd/MM/yyyy HH:mm", referenceDate);
                if (isValid(full)) return full;

                let short = parse(v, "dd/MM/yyyy", referenceDate);
                if (isValid(short)) return short;

                let timeWithSeconds = parse(v, "HH:mm:ss", referenceDate);
                if (isValid(timeWithSeconds)) return timeWithSeconds;

                let time = parse(v, "HH:mm", referenceDate);
                if (isValid(time)) return time;
            }

            return new Date("invalid");
        };

        const isStartTimeOnly = typeof startVal === "string" && isValidTimeOnly(startVal);
        const isEndTimeOnly = typeof endVal === "string" && isValidTimeOnly(endVal);
        let dtStart, dtEnd;

        if (isStartTimeOnly && isEndTimeOnly) {
            const baseDate = format(new Date(), "dd/MM/yyyy");

            dtStart = toDate(`${baseDate} ${startVal}`);
            dtEnd = toDate(`${baseDate} ${endVal}`);
        } else {

            dtStart = toDate(startVal);
            dtEnd = toDate(endVal);
        }

        if ((!isValid(dtStart) || !isValid(dtEnd)) && obligatory) {
            return { ok: false, message: "Data/hora inválida." };
        }

        if (isAfter(dtStart, dtEnd)) {
            return {
                ok: false,
                message: "A data inicial não pode ser maior que a final."
            };
        }

        if (periodValue) {
            let limitDate;

            if (periodType === "minute" || periodType === "minutes")
                limitDate = addMinutes(dtStart, periodValue);

            if (periodType === "hour" || periodType === "hours")
                limitDate = addHours(dtStart, periodValue);

            if (periodType === "day" || periodType === "days")
                limitDate = addDays(dtStart, periodValue);

            if (periodType === "month" || periodType === "months")
                limitDate = addMonths(dtStart, periodValue);

            if (periodType === "year" || periodType === "years")
                limitDate = addYears(dtStart, periodValue);

            if (limitDate && isAfter(startOfMinute(dtEnd), startOfMinute(limitDate))) {

                const label = {
                    minutes: "minutos",
                    minute: "minuto",
                    hours: "horas",
                    hour: "hora",
                    days: "dias",
                    day: "dia",
                    months: "meses",
                    month: "mês",
                    years: "anos",
                    year: "ano"
                } [periodType];

                return {
                    ok: false,
                    message: `O período máximo de pesquisa é de ${periodValue} ${label}.`
                };
            }
        }

        return { ok: true };
    },

    validateOnlyDates(dtStart, dtEnd, periodValue, periodType, customNameDtStart = null, customNameDtEnd = null) {

        if (!dtStart) return { ok: false, message: `O campo "${customNameDtStart ?? "Data inicial"}" é obrigatório.` };
        if (!dtEnd) return { ok: false, message: `O campo "${customNameDtEnd ?? "Data final"}" é obrigatório.` };

        const start = startOfDay(dtStart);
        const end = endOfDay(dtEnd);

        if (isBefore(end, start)) {
            return {
                ok: false,
                message: "A data inicial não pode ser maior que a final."
            };
        }

        if (periodValue) {
            let limitDate;

            const dStart = startOfDay(new Date(dtStart));
            const dEnd = startOfDay(new Date(dtEnd));

            if (periodType === "day" || periodType === "days") limitDate = addDays(dStart, periodValue);
            if (periodType === "month" || periodType === "months") limitDate = addMonths(dStart, periodValue);
            if (periodType === "year" || periodType === "years") limitDate = addYears(dStart, periodValue);

            if (limitDate && isAfter(dEnd, limitDate)) {

                const label = {
                    days: "dias",
                    day: "dia",
                    months: "meses",
                    month: "mês",
                    years: "anos",
                    year: "ano"
                } [periodType];

                return {
                    ok: false,
                    message: `O período máximo de pesquisa é de ${periodValue} ${label}.`
                };
            }
        }

        return { ok: true };
    },

    validateTimeString: function (hrStart, hrEnd, onlyString = false) {

        if (!hrStart) return { ok: false, message: 'O campo "Hora inicial" é obrigatório.' };
        if (!hrEnd) return { ok: false, message: 'O campo "Hora final" é obrigatório.' };

        const fail = {
            ok: false,
            message: "O horário inicial não pode ser maior que o final."
        }

        if (onlyString) {
            const [hStart, mStart, sStart = "00"] = hrStart.split(":");
            const [hEnd, mEnd, sEnd = "00"] = hrEnd.split(":");

            const start =
              Number(hStart) * 3600 +
              Number(mStart) * 60 +
              Number(sStart);

            const end =
              Number(hEnd) * 3600 +
              Number(mEnd) * 60 +
              Number(sEnd);

            if (start > end) return fail;
            return { ok: true };
        } else {
            const getSecondsOfDay = (date) => {
                const h = date.getHours() * 3600;
                const m = date.getMinutes() * 60;
                const s = date.getSeconds();
                return h + m + s;
            };

            const secondsStart = getSecondsOfDay(hrStart);
            const secondsEnd = getSecondsOfDay(hrEnd);

            if (secondsStart > secondsEnd) return fail;
            return { ok: true };
        }
    },

    validateHourOnly (hrStart, hrEnd, setLoading, dateType = 'normal') {
        if (!hrStart || hrStart.length === 0) {
            ReportUtils.alertErrorMessage(`O campo "Hora inicial" é obrigatório.`);
            setLoading(false);
            return false;
        }

        if (!hrEnd || hrEnd.length === 0) {
            ReportUtils.alertErrorMessage(`O campo "Hora final" é obrigatório.`);
            setLoading(false);
            return false;
        }

        if ((hrStart > hrEnd) && dateType === 'normal') {
            ReportUtils.alertErrorMessage('O horário inicial não pode ser maior que o final.');
            setLoading(false);
            return false;
        }

        return true;
    },

    periods () {
        return [
            {value: "all", label: "Todos"},
            {value: "dawn", label: "Madrugada"},
            {value: "morning", label: "Manhã"},
            {value: "afternoon", label: "Tarde"},
            {value: "evening", label: "Noite"},
        ]
    },

    periodChange (value, setFilters, onlyHour = false) {
        let startHour = 0;
        let startMin = 0;
        let startSec = 0;
        let endHour = 23;
        let endMin = 59;
        let endSec = 59;

        const valueToCheck = value instanceof Object ? value.value : value

        switch (valueToCheck) {
            case 'dawn':
                startHour = 0; endHour = 5;
                break;

            case 'morning':
                startHour = 6; endHour = 11;
                break;

            case 'afternoon':
                startHour = 12; endHour = 17;
                break;

            case 'evening':
                startHour = 18; endHour = 23;
                break;

            case 'all':
            default:
                startHour = 0; endHour = 23;
        }

        setFilters(prev => {
            if (onlyHour) return {...prev, period: value, hrStart: ReportUtils.formatHourOnly(startHour), hrEnd: ReportUtils.formatHourOnly(endHour)};

            const periodStart = prev.hrStart ? new Date(prev.hrStart) : new Date();
            const periodEnd = prev.hrEnd ? new Date(prev.hrEnd) : new Date();

            periodStart.setHours(startHour, startMin, startSec, 0);
            periodEnd.setHours(endHour, endMin, endSec, 0);

            return {...prev, period: value, hrStart: periodStart, hrEnd: periodEnd};
        });
    },

    requiredFilterValidate (data, value, setLoading) {
        if (!data || data.length === 0) {
            ReportUtils.alertErrorMessage(`O campo "${value}" é obrigatório.`);
            setLoading(false);
            return false;
        }

        return true;
    },

    filters(formState, labels = {}, audit = false) {
        const filters = {};

        Object.entries(formState).forEach(([key, value]) => {
            if (value === "" || value === null || value === undefined) return;
            const label = labels[key] ?? key;

            if (Array.isArray(value)) {
                if (!value.length) return;

                if (typeof value[0] === "object") {
                    const values = value.map(i => i.label ?? i.value);

                    if (audit) {
                        filters[label] = values;
                    } else {
                        filters[label] = values.length === 1 ? values[0] : `${values[0]} + ${values.length - 1} opções selecionadas.`;
                    }

                } else {
                    filters[label] = value;
                }

            }

            else if (typeof value === "object") {
                filters[label] = value.label ?? value.value;
            }

            else {
                filters[label] = value;
            }

        });

        return audit ? JSON.stringify(filters) : filters;
    },

    appendFilters(filters) {
        if (!filters) return;

        const container = document.getElementById("filters-print");
        if (!container) return;

        let entries = [];
        if (Array.isArray(filters)) entries = filters;

        else if (typeof filters === "object") entries = Object.entries(filters);

        let html = `<div class="used-filters">`;

        entries.forEach(([key, value]) => {
            if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) return;
            let displayValue = "";

            if (Array.isArray(value)) {
                const values = value.map(v => v && typeof v === "object" && "label" in v ? v.label : v);

                if (value.length <= 5) {
                    displayValue = values.join(", ");
                } else {
                    displayValue = `${values[0]} + ${values.length - 1} opções selecionadas`;
                }
            } else if (typeof value === "boolean") {
                displayValue = value ? "Sim" : "Não";
            } else {
                displayValue = value;
            }

            html += `<span class="filter-item" style="padding-right: 1mm"><b>${key}:</b> ${displayValue}</span> `;
        });

        html += `</div>`;
        container.innerHTML = html;
    },

    formatText(text) {
        if (!text) return "";
        if(text === "Não informado") return text;

        const ignoredWords = ['à', 'a', 'e', 'i', 'o', 'u',
                                     'da', 'de', 'di', 'do', 'das',
                                     'des', 'dos', 'para', 'pelo', 'ao',
                                     'até', 'não', 'na', 'no', 'nas', 'nos',
                                     'ou', 'por', 'pela', 'com', 'sem'];

        const words = text.trim().toLowerCase().split(/\s+/);

        return words.map((word, index) => {
            if (word.length === 0) return "";
            if (index === 0) return word[0].toUpperCase() + word.slice(1);
            if (ignoredWords.includes(word)) return word;
            return word[0].toUpperCase() + word.slice(1);
        }).join(" ");
    },

    formatPhone: (phone) => {
        if (!phone) return phone;
        const value = phone.toString().replace(/\D/g, '');

        if (value.length === 11) return value.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
        if (value.length === 10) return value.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");

        return phone;
    },

    formatObjectString(v) {
        if (v === null || v === undefined) return "";

        const formatValue = (str) => typeof str === "string" ? str.replace(/,\s*$/, "") : str;

        const formatDate = (value) => {
            if (typeof value !== "string") return value;
            const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
            if (!isoRegex.test(value)) return value;

            const date = new Date(value);
            if (isNaN(date)) return value;

            return date.toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        };

        if (typeof v === "object") {
            if (Array.isArray(v)) return formatDate(formatValue(JSON.stringify(v)));

            return formatValue(
                Object.values(v).map(i => typeof i === "string" ? formatDate(formatValue(i)) : i).join(", ")
            );
        }

        return formatDate(formatValue(v));
    },

    formatDateTime (date) {
        return date ? format(date, "dd/MM/yyyy HH:mm") : null;
    },

    formatDateOnly(date) {
        if (!date) return null;

        let dateObj;

        if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split("-");
            dateObj = new Date(Number(year), Number(month) - 1, Number(day));
        } else {
            dateObj = new Date(date);
        }

        return format(dateObj, "dd/MM/yyyy");
    },

    formatHourMin (date, hasSeconds = true) {
        const mask = hasSeconds ? "HH:mm:ss" : "HH:mm";
        return date ? format(date, mask) : null;
    },

    formatHourOnly (value) {
        if (value === "") return "";

        let num = parseInt(value, 10);
        if (isNaN(num)) return "";
        if (num > 23) num = 23;

        return num.toString().padStart(2, '0');
    },

    formatDateTimeLabel (date, uncertain) {
        if (!date) return "";

        const d = new Date(date);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        if (uncertain === "true") return `${day}/${month}/${year}`;

        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    formatDateLabel(date) {
        if (!date) return '-';

        if (!date.includes('-')) return date;

        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
    },

    expired(expiration) {
        if (!expiration) return { text: '-', expired: false };

        const isExpired = expiration.includes('vencido');
        const cleanText = expiration.replace('vencido', '').trim();

        return {text: cleanText, expired: isExpired};
    },

    handleHourChange (value) {
        let result = value.replace(/\D/g, '');
        if (result.length > 2) result = result.slice(0, 2);
        return result;
    },

    textLimit (text, max = 60) {
        if (!text) return "";
        return text.length > max ? `${text.slice(0, max)}...` : text;
    },

    print (landscape = false) {
        if (landscape) {
            const style = document.createElement("style");
            style.id = "landscape-style";
            style.innerHTML = "@media print { @page { size: a3 landscape !important; } }";
            document.head.appendChild(style);
        }

        window.print();

        const style = document.getElementById("landscape-style");
        if (style) style.remove();
    },

    waitMapIdle (map) {
        return new Promise((resolve) => {
            if (!map || !window.google) return resolve();

            const listener = map.addListener("idle", () => {
                window.google.maps.event.removeListener(listener);
                resolve();
            });
        });
    },

    async generateMapImage({nodeId, setMapImage, setShowMap, printRef, htmlToImageLib, ignoreElements, preserveMapStyle = false, cacheBust = true}) {
        try {
            const node = document.getElementById(nodeId);
            if (!node) return;

            const mapNodes = node.querySelectorAll("[id^='map-report-component']");
            const maps = [];

            node.classList.add("printing-map");

            mapNodes.forEach(map => {
                if (map.__google_map_instance) {
                    maps.push(map.__google_map_instance);

                    const mapOptions = {
                        gestureHandling: "none",
                        draggable: false,
                        zoomControl: false,
                        scrollwheel: false,
                        disableDefaultUI: true
                    };

                    if (!preserveMapStyle) mapOptions.styles = [];

                    map.__google_map_instance.setOptions(mapOptions);
                }
            });

            await Promise.all(maps.map(m => this.waitMapIdle(m)));
            await new Promise(r => setTimeout(r, 300));

            const dataUrl = await htmlToImageLib.toPng(node, {
                pixelRatio: 2,
                cacheBust,
                useCORS: true,
                skipAutoScale: true,
                skipFonts: true,
                includeQueryParams: true,
                removeContainer: true,
                ignoreElements: ignoreElements
            });

            setMapImage?.(dataUrl);

            node.classList.remove("printing-map");
            setShowMap?.(false);

            await new Promise(r => setTimeout(r, 100));
            await printRef?.current?.print();

        } catch (err) {
            console.error(err);
        } finally {
            setTimeout(() => {
                setMapImage?.(null);

                setTimeout(() => {
                    setShowMap?.(true);
                }, 50);
            }, 500);
        }
    },

    /**
     * @param {boolean} exportInSheets - true = XLSX, false = CSV
     * @param customFileName
     */
    async export ({ exportInSheets = false, customFileName = '' } = {}) {
        this.materializeExportableTables();
        const tables = document.querySelectorAll("table.export-data");

        if (!tables.length) {
            console.warn("Erro ao exportar documento.");
            return;
        }

        const validCustomFileName = customFileName === '' ? null : customFileName;
        if (exportInSheets) {
            try {
                return await this.exportSheetsFromTables(tables, validCustomFileName);
            } finally {
                this.removeMaterializedExportableTables();
            }
        }

        try {
            this.exportCSVFromTable(tables[0], validCustomFileName);
        } finally {
            this.removeMaterializedExportableTables();
        }
    },

    exportCSVFromTable(table, customFileName = null) {
        const rows = [];

        table.querySelectorAll("tr").forEach(tr => {
            const row = [];
            tr.querySelectorAll("th, td").forEach(cell => {
                row.push(this.wrapTextIfNeeded(cell.innerText.trim()));
            });
            rows.push(row);
        });

        this.exportTableToCSV(rows, (customFileName ?? "report.csv"));
    },

    async exportSheetsFromTables(tables, customFileName = null) {
        const sheets = Array.from(tables).map(table => {
            const sheetName = table.getAttribute("sheet-name") || "Relatório";
            const rows = [];

            table.querySelectorAll("tr").forEach(tr => {
                const row = [];
                tr.querySelectorAll("th, td").forEach(cell => {
                    row.push(cell.innerText.trim());
                });
                rows.push(row);
            });

            return { name: sheetName, rows };
        });

        await exportXlsx({ fileName: customFileName ?? "report.xlsx", sheets });
    },

    escapeDoubleQuotes(text) {
        return String(text).replace(/"/g, '""');
    },

    wrapTextIfNeeded(text = "") {
        text = String(text);
        if (text.includes("\n") || text.includes(";") || text.includes('"')) { return `"${this.escapeDoubleQuotes(text)}"`; }
        return text;
    },

    exportTableToCSV(tableArray, filename) {
        const csv = tableArray
            .map(row =>
                row.map(cell => this.wrapTextIfNeeded(cell)).join(";")
            ).join("\r\n");

        const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();

        URL.revokeObjectURL(url);
    },

    createExportableTable (labels, values, sheetName, tableId = null, customHeaders = null) {
        if (!sheetName) return;

        exportableTableDefinitions.set(sheetName, {
            labels: Array.isArray(labels) ? [...labels] : labels,
            values: Array.isArray(values) ? values.map(row => Array.isArray(row) ? [...row] : row) : values,
            sheetName,
            tableId,
            customHeaders: Array.isArray(customHeaders) ? [...customHeaders] : customHeaders
        });
    },

    materializeExportableTables() {
        const container = document.getElementById("chart-tables");
        if (!container) return;

        exportableTableDefinitions.forEach(definition => this.createExportableTableElement(definition));
    },

    createExportableTableElement({labels, values, sheetName, tableId, customHeaders}) {
        const container = document.getElementById("chart-tables");
        if (!container) return;

        const existingTable = container.querySelector(`table[sheet-name="${sheetName}"]`);
        if (existingTable) existingTable.remove();

        const table = document.createElement("table");
        table.classList.add("export-data");
        table.setAttribute("data-report-utils-generated", "true");
        table.setAttribute("sheet-name", sheetName);
        table.style.display = "none";

        const thead = document.createElement("thead");
        const headRow = document.createElement("tr");
        let columns = [];

        if (tableId) {
            const tableThead = document.querySelector(`#${tableId} thead`);
            if (tableThead) columns = Array.from(tableThead.querySelectorAll("th:not(.export-ignore)")).map(th => th.innerText.trim());
        }

        else if (labels && Array.isArray(labels) && values && Array.isArray(values) && !Array.isArray(values[0])) {
            columns = ["Descrição", "Quantidade"];
        }

        else if (Array.isArray(values) && Array.isArray(values[0])) {
            if (Array.isArray(labels)) {
                columns = labels;
            } else {
                columns = values[0].map((_, i) =>
                    i === 0 ? "Categoria" : "Total"
                );
            }
        }

        if (!columns.length) columns = ["Valor"];
        if (customHeaders) columns = customHeaders;

        columns.forEach(col => {
            const th = document.createElement("th");
            th.innerText = col;
            headRow.appendChild(th);
        });

        thead.appendChild(headRow);
        table.appendChild(thead);

        const tbody = document.createElement("tbody");

        if (labels && values && !tableId && !Array.isArray(values[0])) {
            labels.forEach((label, i) => {
                const tr = document.createElement("tr");

                tr.innerHTML = `
                    <td>${label ?? ""}</td>
                    <td>${values[i] ?? ""}</td>
                `;

                tbody.appendChild(tr);
            });
        }

        else if (Array.isArray(values) && Array.isArray(values[0])) {
            values.forEach(row => {
                const tr = document.createElement("tr");

                row.forEach(cell => {
                    const td = document.createElement("td");
                    td.innerText = cell?.value ?? cell ?? "";
                    tr.appendChild(td);
                });

                tbody.appendChild(tr);
            });
        }

        else if (tableId) {
            document
                .querySelectorAll(`#${tableId} > tbody > tr`)
                .forEach(r => {
                    if (r.classList.contains("export-ignore")) return;
                    const tr = document.createElement("tr");

                    r.querySelectorAll("td:not(.export-ignore)").forEach(c => {
                        const td = document.createElement("td");
                        td.innerText = c.innerText.trim();
                        tr.appendChild(td);
                    });

                    tbody.appendChild(tr);
                });
        }

        table.appendChild(tbody);
        container.appendChild(table);
    },

    removeMaterializedExportableTables() {
        document.querySelectorAll("table.export-data[data-report-utils-generated='true']")
            .forEach(table => table.remove());
    },

    clearExportableTables() {
        exportableTableDefinitions.clear();
        this.removeMaterializedExportableTables();
    },

    openXRay(navigate, url, garrison) {
        if (!garrison?.garrisonid) return;

        let team = [garrison?.garrison_team].filter(Boolean);
        if (!team.length) team = ['WITHOUT-TEAM'];

        const params = {
            dtStart: garrison?.garrison_start ?? "",
            dtEnd: garrison?.garrison_end ?? ""
        };

        if (url !== 'encounter_x_ray') params.team = JSON.stringify(team);

        const query = createSearchParams(params).toString();
        navigate(`/reports/${url}?${query}`, true);
    },

    getUrlParam(key) {
        return new URLSearchParams(window.location.search).get(key);
    },

    buildUrlFilters({ initialFilters, includeTeam = false }) {
        const dtStartParam = this.getUrlParam("dtStart");
        const dtEndParam = this.getUrlParam("dtEnd");
        const teamParam = this.getUrlParam("team");
        const filters = initialFilters();

        return {
            ...filters,
            dtStart: dtStartParam ? parse(dtStartParam, "dd/MM/yyyy HH:mm", new Date()) : filters.dtStart,
            dtEnd: dtEndParam ? parse(dtEndParam, "dd/MM/yyyy HH:mm", new Date()) : filters.dtEnd,
            ...(includeTeam && { team: teamParam ? JSON.parse(teamParam).map(item => ({label: item === "WITHOUT-TEAM" ? "Não informado" : item, value: item })) : []})
        };
    },

    clearUrlFilters () {
        window.history.replaceState({}, document.title, window.location.pathname);
    },

    async fetchReport({currentFilters, setLoading, setReportData, setCollapsed, url, buildFilters, buildAuditFilters, validatePeriod}) {
        this.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData?.(null)
            }
        });

        try {
            const periodValidation = validatePeriod(currentFilters);

            if (!periodValidation.ok) {
                this.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = buildFilters(currentFilters, this);
            const auditFilters = buildAuditFilters(currentFilters, formattedFilters, this);

            const { data: response } = await axios.post(`${API_URL}${url}`, {
                ...formattedFilters,
                filters: auditFilters
            });

            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed?.(true);

        } catch (error) {
            this.catchErrors(error, { setLoading });
        } finally {
            setLoading(false);
        }
    },

    withoutContentReadjust() {
        const tds = document.querySelectorAll("table:not(.export-data) td");

        tds.forEach(td => {
            const text = td.innerText.trim();

            if (text === "Não informado" || text === "Inconsistente" || text === "Não cadastrado") {
                td.style.color = "#8F8F8F";
                td.style.fontStyle = "italic";
            }
        });
    },

    buildStackedData({data = [], base, bars, amountValues, top = null, hasLimit = false}) {
        const matrix = {};
        const totals = {};
        const seriesTotals = {};
        const seriesSet = new Set();

        data.forEach(item => {
            const category = item[base];
            const series = item[bars];
            const value = parseInt(item[amountValues], 10) || 0;

            if (!category || !series) return;
            seriesSet.add(series);

            if (!matrix[series]) matrix[series] = {};
            matrix[series][category] = (matrix[series][category] || 0) + value;

            totals[category] = (totals[category] || 0) + value;
            seriesTotals[series] = (seriesTotals[series] || 0) + value;
        });

        let categories = Object.keys(totals);
        const extendedCategories = hasLimit ? [...categories] : null;
        if (top && top > 0) categories = categories.slice(0, top);

        const seriesList = Array.from(seriesSet);
        const values = seriesList.map(series => {
            const row = [series];

            categories.forEach(cat => {
                row.push(matrix[series]?.[cat] || 0);
            });

            return row;
        });

        let extendedValues = null;

        if (hasLimit) {
            extendedValues = seriesList.map(series => {
                const row = [series];

                extendedCategories.forEach(cat => {
                    row.push(matrix[series]?.[cat] || 0);
                });

                return row;
            });
        }

        return {
            labels: categories,
            values,
            totals,

            ...(hasLimit && {
                extendedLabels: extendedCategories,
                extendedValues
            })
        };
    }
}

export default ReportUtils;